import React from 'react';
import { connect } from 'react-redux';
import { get, filter, map, isEmpty, isNil } from 'lodash';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import { bindActionCreators } from 'redux';

// local dependencies
import { authFlowMemberVerificationStep as AuthFlowMemberVerificationStepView } from 'ui/views/widgets/authFlowMemberVerificationStep';
import { SERVICE_RESPONSE_STATUS, CALL_ACTIVITY_STATE } from 'constants/common';
import { AuthFlowMemberVerificationStepService } from 'services/authFlowMemberVerificationStep';
import { REDUCER } from 'constants/entity-names';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import { addVerifiedMember, updateCallState } from 'store/actions/callState';
import { clearMemberVerificationState } from 'store/actions/updateMemberVerificationState';
import { GlobalService } from 'services/global';

// local dependency
import { getStore } from 'store';

// get store
const store = getStore();

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { AuthFlowMemberVerificationStep } from 'ui/containers/AuthFlowMemberVerificationStep';
 *
 * // render
 * <AuthFlowMemberVerificationStep propName='propValue'/>
 */
class _AuthFlowMemberVerificationStep extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );
        
     // const patientId=  get( props, 'callState.callerInfo.patientId' );

        // bind methods to instance
        this.handleMarkVerified = this.handleMarkVerified.bind( this );
        this.handlePrevActionClick = this.handlePrevActionClick.bind( this );
        this.handleMemberSelection = this.handleMemberSelection.bind( this );
        this.handleNextActionClick = this.handleNextActionClick.bind( this );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            processStatus: {
                isMemberVerified: false, // when criteria widget returns verified
            },
            memberId: this.getDefaultMemberId(),
            patientId: get( props, 'callState.callerInfo.patientId' )
        };

        // create service instance
        this.authFlowMemberVerificationStepService = new AuthFlowMemberVerificationStepService();
        this.globalService = new GlobalService();
        
        // start component data fetch
        this.fetchComponentData();
    }

    /*********************************************/

    /**
     * @desc Extract default member ID.
     * 2. While verifying another member, the `memberId` would be provided through the URL query string parameter.
     * 1. When caller is `member`, `memberId` would be provided with the `callerInfo.refId` of the ongoing call data.
     * 3. When user lands on verification page, post epost enrollment, user info can be accessed from store `MEMBER_VERIFICATION_STATE`
     */
    getDefaultMemberId() {
        log.debug( 'AuthFlowMemberVerificationStep.getDefaultMemberId()' );

        const memberVerificationState = store.getState()[ REDUCER.MEMBER_VERIFICATION_STATE ];

        if( ! isEmpty( memberVerificationState ) ) {
            if( ! isNil( get( memberVerificationState, 'memberId', null ) ) ) {
                return get( memberVerificationState, 'memberId' );
            }
        }

        // parse query string params
        const queryParams = queryString.parse( this.props.location.search );

        /*----------*/

        // if verifying another member, default member ID would be `null`
        if( ! isEmpty( get( queryParams, 'verifyOther' ) ) ) {
            return null;
        }

        /*----------*/

        // get member id from the query string
        if( ! isEmpty( get( this.props.location, 'state.memberId', undefined ) ) ) {
            return get( this.props.location, 'state.memberId', undefined );
        }

        /*----------*/

        // get member id from the previous (caller identification step)
        const prevStepData = this.props.getStepSpecificData( this.props.activeStepIndex - 1 );
        if( ! isEmpty( get( prevStepData, 'memberId' ) ) ) {
            return get( prevStepData, 'memberId' );
        }

        /*----------*/

        // get member id from the ongoing call data
        if( ! isEmpty( get( this.props, 'callState.callerInfo.refId' ) ) ) {
            return get( this.props, 'callState.callerInfo.refId' );
        }

        /*----------*/

        // return `null` when no member id is found
        return null;
    }

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'AuthFlowMemberVerificationStep.fetchComponentData()' );

        // cancel ongoing request
        this.authFlowMemberVerificationStepService.cancel();

        // get status and service data
        const { status, data } = await this.authFlowMemberVerificationStepService.fetchComponentData( {
            url: this.props.widget.url,
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
            } );
        }
    }

    /**
     * @desc Filter widgets to display based on `state.processStatus`.
     */
    getVisibleWidgets() {
        log.debug( 'AuthFlowMemberVerificationStep.getVisibleWidgets()' );

        // get widgets from component data
        const widgets = get( this.state, 'componentData.widgets', [] );

        // get preverified members
        const preVerifiedMembers = get( this.props, 'callState.members', [] );
        const preVerifiedPatient = get( this.props, 'callState.members', [] );

        // get current `memberId` from the state
        const memberId = get( this.state, 'memberId' );
        const patientId = get( this.state, 'patientId' );

        return filter( widgets, ( widget ) => {
            // previously return `pre-verified members widget` if `members` array is non-empty in the ongoing call data, which means least one member is verified
            // return `pre-verified members widget` if `patientId` array is non-empty in the ongoing call data, which means least one member is verified
            // if( ( 'authFlowMemberVerificationPreVerifiedMembers' === widget.name ) && ! isEmpty( preVerifiedMembers ) ) {
            if( ( 'authFlowMemberVerificationPreVerifiedMembers' === widget.name ) && ! isEmpty( preVerifiedPatient ) ) {
                return true;
            }

            /*----------------*/

            //previously  display `member search widget` if `memberId` is empty (i.e. member hasn't been identified yet)
            // display `patientId search widget` if `patientId` is empty (i.e. member hasn't been identified yet)
            if( ( 'authFlowMemberVerificationMemberSearch' === widget.name ) ) {
                return true;
            }

            /*----------------*/

            // display `match criteria widget` if member has been identified
            // if( ( 'authFlowMemberVerificationMatchCriteria' === widget.name ) && ! isEmpty( memberId ) ) {
            if( ( 'authFlowMemberVerificationMatchCriteria' === widget.name ) && (patientId != null || memberId != null) ) {
                return true;
            }

            /*----------------*/

            // previously display `family unit widget` if member has been identified and verified
            // display `family unit widget` if patientId has been identified and verified
            // if( ( 'authFlowMemberVerificationFamilyUnit' === widget.name ) && ! isEmpty( memberId ) /* && get( this.state, 'processStatus.isMemberVerified' ) */ ) {
            if( ( 'authFlowMemberVerificationFamilyUnit' === widget.name ) && (patientId != null || memberId != null) /* && get( this.state, 'processStatus.isMemberVerified' ) */ ) {
                return true;
            }

            /*----------------*/

            //previously  display `Hipaa auth widget` if member has been identified and verified
            // display `Hipaa auth widget` if patientId has been identified and verified
            // if( ( 'authFlowMemberVerificationHipaaAuth' === widget.name ) && ! isEmpty( memberId ) /* && get( this.state, 'processStatus.isMemberVerified' ) */ ) {
            if( ( 'authFlowMemberVerificationHipaaAuth' === widget.name ) && (patientId != null || memberId != null) /* && get( this.state, 'processStatus.isMemberVerified' ) */ ) {
                return true;
            }

            /*----------------*/

            // if none of the conditions match, do not include the widget
            return false;
        } );
    }

    /**
     * @desc Get (format) navigation actions.
     */
    getNavAction() {
        log.debug( 'AuthFlowMemberVerificationStep.getNavAction()' );

        // get `filtered` actions from component data
        const navActions = filter( get( this.state, 'componentData.navActions', [] ), ( navAction ) => {

            // 🚫 exclude `prev` action unconditionally
            if( 'prev' === navAction.name ) {
                return false;
            }

            return true;
        } );

        // return formatted actions
        return map( navActions, navAction => {
            
            // add `title` and `onClick` handler for the `prev` button
            if( 'prev' === navAction.name ) {
                return {
                    ...navAction,
                    onClick: this.handlePrevActionClick,
                    outline: true,
                    title: {
                        refId: 'displayTextPrevMemberSearch',
                        value: ''
                    },
                };
            }

            // add onClick` handler for the `next` button
            if( 'next' === navAction.name ) {
                return {
                    ...navAction,
                    onClick: this.handleNextActionClick,
                };
            }

            return navAction;
        } );
    }

    /**
     * @desc Update current member ID when from member search new member is selected.
     */
    handleMemberSelection( { memberId,patientId } = {} ) {
        log.debug( 'AuthFlowMemberVerificationStep.handleMemberSelection()' );

        this.setState( {
            memberId,patientId
        } );
    }

    /**
     * @desc Mark member as verified (from criteria match widget).
     */
    handleMarkVerified( { isMemberVerified = false, /* isMemberSelected  = false, isSearchVisibleForMember = false */ } ) {
        log.debug( 'AuthFlowMemberVerificationStep.handleMarkVerified()' );

        this.setState( {
            processStatus: {
                ...this.state.processStatus,
                isMemberVerified: isMemberVerified,
            }
        } );
    }

    /**
     * Handle previous button click.
     */
    handlePrevActionClick() {
        log.debug( 'AuthFlowMemberVerificationStep.handlePrevActionClick()' );

        // move to previous step
        this.props.moveBackward();
    }

    /**
     * Handle next button click.
     */
    handleNextActionClick() {
        log.debug( 'AuthFlowMemberVerificationStep.handleNextActionClick()' );

        // add verified memeber to the call state
        this.props.addVerifiedMember( this.state.memberId );

        // update call state in the redux store
        this.props.updateCallState( {
            callState: CALL_ACTIVITY_STATE.ACTIVE,
        } );

        // clean memberverification state
        this.props.clearMemberVerificationState();

        // start calculating alerts
        this.globalService.startAlertsCalculation( { memberId: this.state.memberId, patientId: this.state.patientId } );

        // switch to member details route
        this.props.history.push( {
            pathname: DASHBOARD_SUB_ROUTES.MEMBER_MANAGEMENT,
            state: { memberId: this.state.memberId, patientId: this.state.patientId }
        } );
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.authFlowMemberVerificationStepService.cancel(); // cancel ongoing requests
        this.globalService.cancel();
    }

    /**
     * @desc Render a React view component
     */
    render() {
        
        return (
            <AuthFlowMemberVerificationStepView
                callState={ this.props.callState }
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                widgets={ this.getVisibleWidgets() }
                navActions={ this.getNavAction() }
                onMemberSelection={ this.handleMemberSelection }
                onMarkVerified={ this.handleMarkVerified }
                memberId={ this.state.memberId }
                patientId={ this.state.patientId }
                processStatus={ this.state.processStatus }
            />
        );
    }
}

// set display name
_AuthFlowMemberVerificationStep.displayName = '_AuthFlowMemberVerificationStep';

// set default props
_AuthFlowMemberVerificationStep.defaultProps = {};

// prop types
_AuthFlowMemberVerificationStep.propTypes = {};

/*************************************************/

// map state to props
const mapStateToProps = ( state ) => {
    return {
        callState: state[ REDUCER.CALL_STATE ],
        appContext: state[ REDUCER.APP_CONTEXT ],
    };
};

// map dispatch to props
const mapDispatchToProps = ( dispatch ) => {
    return {
        updateCallState: bindActionCreators( updateCallState, dispatch ),
        addVerifiedMember: bindActionCreators( addVerifiedMember, dispatch ),
        clearMemberVerificationState: bindActionCreators( clearMemberVerificationState, dispatch )
    };
};

// return component with acccess to Redux store
export const AuthFlowMemberVerificationStep = withRouter( connect( mapStateToProps, mapDispatchToProps )( _AuthFlowMemberVerificationStep ) );

